import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BellRing, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Top-bar bell: combines watchdog-alert unread count with in-app
 * notifications (trial reminders, plan activation) in a dropdown.
 */
export default function AlertBell() {
    const [alertUnread, setAlertUnread] = useState(0);
    const [notif, setNotif] = useState({ items: [], unread_count: 0 });
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;
        const fetchAll = () => {
            api.get("/alerts/unread-count")
                .then(({ data }) => { if (!cancelled) setAlertUnread(data.unread_count || 0); })
                .catch(() => {});
            api.get("/notifications")
                .then(({ data }) => { if (!cancelled) setNotif(data); })
                .catch(() => {});
        };
        fetchAll();
        const id = setInterval(fetchAll, 60000);
        return () => { cancelled = true; clearInterval(id); };
    }, [location.pathname]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const unread = alertUnread + (notif.unread_count || 0);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && notif.unread_count > 0) {
            api.post("/notifications/read-all").catch(() => {});
            setNotif((n) => ({ ...n, unread_count: 0, items: n.items.map((i) => ({ ...i, read: true })) }));
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={toggle}
                className="relative inline-flex items-center justify-center w-10 h-10 border border-[#0D1B3E]/15 bg-white hover:border-[#C9A84C] transition-colors"
                aria-label="Open notifications"
                data-testid="topbar-alert-bell"
            >
                <BellRing size={17} strokeWidth={1.6} className="text-[#0D1B3E]" />
                {unread > 0 && (
                    <span
                        className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 grid place-items-center bg-[#C9A84C] text-[#0D1B3E] text-[10px] font-bold rounded-sm"
                        data-testid="topbar-alert-badge"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-12 z-50 w-[320px] sm:w-[360px] bg-white border border-[#0D1B3E]/15 shadow-xl"
                    data-testid="notifications-dropdown"
                >
                    <div className="px-4 py-3 border-b border-[#0D1B3E]/10 label-eyebrow">
                        Notifications
                    </div>
                    <div className="max-h-[340px] overflow-y-auto">
                        {notif.items.length === 0 ? (
                            <div className="px-4 py-6 text-sm text-[#0D1B3E]/60" data-testid="notifications-empty">
                                No notifications yet.
                            </div>
                        ) : (
                            notif.items.map((n) => (
                                <div
                                    key={n.id}
                                    className="px-4 py-3 border-b border-[#0D1B3E]/5"
                                    data-testid={`notification-item-${n.kind}`}
                                >
                                    <div className="flex items-start gap-2">
                                        <Sparkles size={14} color="#C9A84C" className="mt-0.5 shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium">{n.title}</div>
                                            <p className="text-xs text-[#0D1B3E]/65 mt-0.5">{n.body}</p>
                                            {n.cta_path && (
                                                <Link
                                                    to={n.cta_path}
                                                    onClick={() => setOpen(false)}
                                                    className="inline-flex items-center gap-1 text-xs text-[#C9A84C] hover:underline mt-1.5"
                                                    data-testid={`notification-cta-${n.kind}`}
                                                >
                                                    {n.kind.startsWith("trial") ? "Upgrade now" : "Open"} <ArrowRight size={11} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    <Link
                        to="/app/watchdogs"
                        onClick={() => setOpen(false)}
                        className="block px-4 py-3 text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] border-t border-[#0D1B3E]/10"
                        data-testid="notifications-watchdog-link"
                    >
                        Watchdog alerts{alertUnread > 0 ? ` (${alertUnread} unread)` : ""} →
                    </Link>
                </div>
            )}
        </div>
    );
}
